import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import CustomDatePicker from '@/components/shared/inputs/CustomDatePicker/CustomDatePicker';
import { createDateSafe } from '@/utils/dateUtils';
import { brToIsoDate, isoToBrDate } from '../../../utils/vacationDates';

export interface IsoDatePickerProps {
    value: string;
    onChange: (isoDate: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    id?: string;
    allowedDates?: string[];
    isDateSelectable?: (isoDate: string) => boolean;
    defaultMonthIso?: string;
}

export function IsoDatePicker({
    value,
    onChange,
    onBlur,
    disabled,
    id,
    allowedDates,
    isDateSelectable,
    defaultMonthIso,
}: IsoDatePickerProps) {
    const [text, setText] = useState(() => isoToBrDate(value));
    const lastEmitted = useRef(value);

    useEffect(() => {
        if (value !== lastEmitted.current) {
            setText(isoToBrDate(value));
            lastEmitted.current = value;
        }
    }, [value]);

    const handleChange = (nextText: string) => {
        setText(nextText);
        const iso = brToIsoDate(nextText);
        if (iso) {
            lastEmitted.current = iso;
            onChange(iso);
        }
    };

    const isDateAllowed = useMemo(() => {
        if (!allowedDates && !isDateSelectable) return undefined;

        return (date: Date) => {
            const iso = format(date, 'yyyy-MM-dd');
            if (allowedDates) return allowedDates.includes(iso);
            return isDateSelectable ? isDateSelectable(iso) : true;
        };
    }, [allowedDates, isDateSelectable]);

    const defaultMonth = useMemo(() => {
        const iso = allowedDates?.[0] ?? defaultMonthIso;
        return iso ? (createDateSafe(iso) ?? undefined) : undefined;
    }, [allowedDates, defaultMonthIso]);

    const highlightDate = useMemo(() => {
        if (!allowedDates) return undefined;
        return (date: Date) => allowedDates.includes(format(date, 'yyyy-MM-dd'));
    }, [allowedDates]);

    return (
        <CustomDatePicker
            id={id}
            value={text}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            placeholder="dd/mm/aaaa"
            isDateSelectable={isDateAllowed}
            defaultMonth={defaultMonth}
            highlightDate={highlightDate}
            hideTodayHighlight
        />
    );
}

export default IsoDatePicker;
